const API_BASE = '/api/compliance/evidence-expiry';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

function getAuthHeaders(): HeadersInit {
  const token = sessionStorage.getItem('impersonation_token') || localStorage.getItem('token');
  if (!token) throw new Error('No authentication token found');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(err.error || err.message || `API Error: ${res.status}`);
  }
  return res.json();
}

export interface ExpiringEvidence {
  id: number;
  file_name: string;
  compliance_page_id: number;
  page_title?: string;
  expires_at: string;
  days_remaining: number;
  renewal_required: boolean;
}

export interface RenewalTask {
  id: number;
  evidence_item_id: number;
  evidence_name?: string;
  compliance_page_id: number;
  company_id: number;
  assigned_to?: number;
  due_date?: string;
  status: 'pending' | 'in_progress' | 'completed';
}

export interface ValidityPeriod {
  id: number;
  category: string;
  validity_days: number;
  review_days_before: number;
  company_id?: number;
}

export interface ExpiryDashboard {
  expiring_30_days: number;
  expiring_7_days: number;
  expired: number;
  renewal_tasks_pending: number;
}

export const evidenceExpiryApi = {
  async getExpiring(days = 30, companyId?: number): Promise<ExpiringEvidence[]> {
    const q = new URLSearchParams({ days: String(days) });
    if (companyId) q.set('company_id', String(companyId));
    const res = await fetch(`${API_BASE}/expiring?${q}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getExpired(companyId?: number): Promise<ExpiringEvidence[]> {
    const q = companyId ? `?company_id=${companyId}` : '';
    const res = await fetch(`${API_BASE}/expired${q}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getDashboard(companyId?: number): Promise<ExpiryDashboard> {
    const q = companyId ? `?company_id=${companyId}` : '';
    const res = await fetch(`${API_BASE}/dashboard${q}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async createRenewalTask(data: { evidence_item_id: number; compliance_page_id: number; company_id: number; assigned_to?: number; due_date?: string }): Promise<RenewalTask> {
    const res = await fetch(`${API_BASE}/renewal-tasks`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async listRenewalTasks(companyId?: number): Promise<RenewalTask[]> {
    const q = companyId ? `?company_id=${companyId}` : '';
    const res = await fetch(`${API_BASE}/renewal-tasks${q}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async completeRenewalTask(id: number): Promise<void> {
    const res = await fetch(`${API_BASE}/renewal-tasks/${id}/complete`, { method: 'PUT', headers: getAuthHeaders() });
    await handleResponse(res);
  },

  async getValidityPeriods(companyId?: number): Promise<ValidityPeriod[]> {
    const q = companyId ? `?company_id=${companyId}` : '';
    const res = await fetch(`${API_BASE}/validity-periods${q}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async updateValidityPeriod(id: number, data: { validity_days: number; review_days_before: number }): Promise<ValidityPeriod> {
    const res = await fetch(`${API_BASE}/validity-periods/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },
};
